"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx } from 'react/jsx-runtime';
import { schemaIsNumber, valueToSchema } from '@membranehq/sdk';
import { EditorTagContextProvider } from '../ComboBoxElements/Editor/context/EditorTagContext.mjs';
import { LexicalProvider } from '../ComboBoxElements/Editor/LexicalEditor.mjs';
import { EditorTagBinding } from '../ComboBoxElements/Editor/nodes/EditorTagBinding.mjs';
import { ComboboxEditorTrigger } from './ComboboxTriggers/ComboboxEditorTrigger.mjs';
import { ComboboxTagTrigger } from './ComboboxTriggers/ComboboxTagTrigger.mjs';
import { ComboBoxProvider } from './context/combobox-context.mjs';
import { ComboBoxDropdownContextProvider } from './context/combobox-dropdown-context.mjs';
import { ComboBoxDropdownSearchProvider } from './context/combobox-dropdown-search.mjs';
import { canUserEnterValueFromKeyboard } from './context/helpers.mjs';
import { NestedComboBoxProvider } from './context/nested-combobox-context.mjs';
import { ValueType } from './value-spec.mjs';

const EMPTY_OPTION_FACTORIES = [];
function ComboBox({
  value,
  schema,
  valueSpec = { type: ValueType.ANY, allowCustom: true },
  variablesSchema,
  variablesOnly = false,
  options = void 0,
  optionFactories = EMPTY_OPTION_FACTORIES,
  onChange,
  valueComponent = void 0,
  placeholder = "",
  isRequired = false,
  disabled = false,
  className,
  hideSearch = false,
  // Dropdown related
  autoOpen = false,
  onOpen,
  onClose,
  topDropdownPanelComponent = void 0,
  refsToIgnoreClick,
  onValueChange: _,
  ...rest
}) {
  const userCanEnterValueFromKeyboard = canUserEnterValueFromKeyboard(
    schema,
    options,
    optionFactories,
    variablesOnly,
    value
  );
  const handleChange = (newValue) => {
    if (newValue !== "" && schema && schemaIsNumber(schema)) {
      onChange(valueToSchema(newValue, schema, { normalizeValues: true }));
      return;
    }
    onChange(newValue);
  };
  const dropdownProps = {
    autoOpen,
    onDropdownOpen: onOpen,
    onDropdownClose: onClose,
    topDropdownPanelComponent
  };
  const props = {
    placeholder,
    className,
    onChange: handleChange,
    required: isRequired,
    disabled,
    ...rest
  };
  return /* @__PURE__ */ jsx(
    ComboBoxProvider,
    {
      value,
      onValueChange: handleChange,
      schema,
      valueSpec,
      variablesSchema,
      options,
      optionFactories,
      refsToIgnoreClick,
      ...dropdownProps,
      children: /* @__PURE__ */ jsx(NestedComboBoxProvider, { component: ComboBox, children: /* @__PURE__ */ jsx(ComboBoxDropdownSearchProvider, { hideSearch, children: /* @__PURE__ */ jsx(ComboBoxDropdownContextProvider, { children: /* @__PURE__ */ jsx(EditorTagContextProvider, { renderer: EditorTagBinding, children: /* @__PURE__ */ jsx(LexicalProvider, { children: userCanEnterValueFromKeyboard ? /* @__PURE__ */ jsx(ComboboxEditorTrigger, { ...props }) : /* @__PURE__ */ jsx(ComboboxTagTrigger, { ...props, valueComponent }) }) }) }) }) })
    }
  );
}

export { ComboBox };
